import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { EmployeeService } from '../../services/employee.service';
import { LoadingService } from '../../services/loading.service';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  uid: string | null = null;
  profile: any = null;
  loading = true;
  newEmail = '';
  saving = false;
  selectedFiles: any;
  uploadInProgress = false;

  constructor(
    private afAuth: AngularFireAuth,
    private db: AngularFireDatabase,
    private snack: MatSnackBar,
    private storage: AngularFireStorage,
    private employeeservice: EmployeeService,
    private isLoading: LoadingService
  ) {}

  async ngOnInit() {
    this.afAuth.authState.pipe(takeUntil(this._destroyed$)).subscribe(async user => {
      if (user) {
        this.uid = user.uid;
        this.newEmail = user.email || '';
        // subscribe to realtime DB profile
        this.db.object(`users/${this.uid}`).valueChanges().pipe(takeUntil(this._destroyed$)).subscribe(profile => {
          this.profile = profile;
          this.loading = false;
        }, err => {
          console.error('Error reading profile:', err);
          this.loading = false;
        });
      } else {
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/images/userbig.png';
  }

  async onFileChange(event: any) {
    if (!this.uid) return;
    this.selectedFiles = event.target.files;
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;
    this.isLoading.setLoading(true);
    this.uploadInProgress = true;
    try {
      const selectedFile = this.selectedFiles[0];
      const filePath = `UserProfileImg/${this.uid}/${selectedFile.name}`;
      const fileRef = this.storage.ref(filePath);
      await fileRef.put(selectedFile);
      const downloadURL = await fileRef.getDownloadURL().toPromise();
      const userData: any = { profileImages: downloadURL };
      Object.keys(userData).forEach((key) => {
        if (userData[key] === undefined) delete userData[key];
      });
      await this.employeeservice.updateUser(this.uid, userData);
      this.snack.open('Profile image updated', undefined, { duration: 3000 });
      // update UI
      this.profile = { ...(this.profile || {}), profileImages: downloadURL };
    } catch (err) {
      console.error('Error uploading image:', err);
      this.snack.open('Error updating profile image', undefined, { duration: 3000 });
    } finally {
      this.uploadInProgress = false;
      this.isLoading.setLoading(false);
    }
  }

  async changeEmail() {
    if (!this.newEmail || !this.uid) {
      this.snack.open('Please enter a valid email', undefined, { duration: 3000 });
      return;
    }
    if (!this.newEmail.includes('@')) {
      this.snack.open('Please enter a valid email address', undefined, { duration: 3000 });
      return;
    }

    this.saving = true;
    try {
      // Ensure we have a fresh token
      const currentUser = await this.afAuth.currentUser;
      if (currentUser) {
        await currentUser.getIdToken(true); // Force refresh token
      }

      const functions = getFunctions(undefined, 'us-central1'); // Specify the region
      const changeEmailCallable = httpsCallable(functions, 'changeOwnEmail'); // Use the modular httpsCallable
      const result = await changeEmailCallable({ newEmail: this.newEmail }); // Await the promise directly
      const res: any = result.data;

      this.snack.open(res?.message || 'Email updated successfully', undefined, { duration: 3000 });

      // refresh token / update local user email
      if (currentUser) {
        await currentUser.getIdToken(true);
        // update local storage userEmail if used elsewhere
        localStorage.setItem('userEmail', this.newEmail);
      }
    } catch (err: any) {
      console.error('changeOwnEmail error', err);
      const msg = err?.message || 'Failed to change email';
      this.snack.open(msg, undefined, { duration: 3000 });
    } finally {
      this.saving = false;
    }
  }
}
